import type {
  AppFeedbackContextDto,
  AppFeedbackReviewStatus,
  AppFeedbackRouting,
  AppFeedbackRoutingOptionsDto,
} from "breuer-supercharged-sdk";
import { backendErrorMessage } from "../../utils/api_error_message.ts";

const ORDER_INBOX_ROUTE = /^\/order_inbox\/detail\/([\w-]{1,64})\/?$/;
const BESTELLUNG_ROUTE = /^\/auftrag\/([\w-]{1,64})\/?$/;

/** Bestelleingangs-Mail bzw. Bestellung, die auf der aktuellen Route geöffnet ist. */
export function parseFeedbackContext(pathname: string): AppFeedbackContextDto {
  const mail = ORDER_INBOX_ROUTE.exec(pathname);
  if (mail) return { incomingEmailJobId: mail[1] };
  const bestellung = BESTELLUNG_ROUTE.exec(pathname);
  if (bestellung) return { bestellungId: bestellung[1] };
  return {};
}

export function hasFeedbackContext(context: AppFeedbackContextDto): boolean {
  return Boolean(context.incomingEmailJobId || context.bestellungId);
}

/** Satz für den Dialog: was automatisch mitgeht. */
export function describeFeedbackContext(context: AppFeedbackContextDto, includesScreenshot: boolean): string {
  const parts: string[] = [];
  if (includesScreenshot) parts.push("Screenshot");
  parts.push("aktuelle Seite");
  if (context.bestellungId) parts.push("geöffnete Bestellung");
  if (context.incomingEmailJobId) parts.push("geöffnete Bestelleingangs-Mail");
  return `Automatisch mitgesendet: ${joinGerman(parts)}.`;
}

function joinGerman(parts: string[]): string {
  if (parts.length <= 1) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} und ${parts[parts.length - 1]}`;
}

/** Die Auswahl „Senden an“ gibt es nur bei aktiver Vorprüfung mit Prüfern. */
export function showsRoutingChoice(options: AppFeedbackRoutingOptionsDto | null | undefined): boolean {
  return Boolean(options?.reviewEnabled && options.reviewers.length > 0);
}

/** Voreinstellung: Prüfer schicken sich nichts selbst zur Prüfung. */
export function defaultFeedbackRouting(options: AppFeedbackRoutingOptionsDto): AppFeedbackRouting {
  return options.currentUserIsReviewer ? "developer" : "review";
}

export type FeedbackRoutingGate = "loading" | "error" | "choice" | "direct";

/**
 * Zustand der Routing-Optionen für den Dialog. Gesperrt ist nur „loading“; bei „error“ geht das Feedback
 * ohne `routing` raus und der Server wendet die Mandanten-Einstellung an (Vorprüfung oder direkt).
 */
export function feedbackRoutingGate(
  options: AppFeedbackRoutingOptionsDto | null | undefined,
  isFetching: boolean,
): FeedbackRoutingGate {
  if (options) return showsRoutingChoice(options) ? "choice" : "direct";
  return isFetching ? "loading" : "error";
}

export function canSubmitFeedback(gate: FeedbackRoutingGate): boolean {
  return gate !== "loading";
}

/** Zu sendendes Routing; `undefined`, wenn der Dialog keine Auswahl zeigt (Mandanten-Einstellung gilt). */
export function resolveFeedbackRouting(
  options: AppFeedbackRoutingOptionsDto | null | undefined,
  userChoice: AppFeedbackRouting | null,
): AppFeedbackRouting | undefined {
  if (!options || !showsRoutingChoice(options)) return undefined;
  return userChoice ?? defaultFeedbackRouting(options);
}

export function feedbackThanksMessage(response: {
  reviewStatus: AppFeedbackReviewStatus;
  reviewerNames: string[];
}): string {
  if (response.reviewStatus !== "pending_review") return "Danke! Ihr Feedback ist bei den Entwicklern.";
  const names = joinGerman(response.reviewerNames);
  return `Danke! Ihr Feedback liegt jetzt bei ${names || "den Prüfern"} zur Prüfung.`;
}

export const FEEDBACK_SEND_FAILED_MESSAGE = "Feedback konnte nicht gesendet werden. Bitte versuchen Sie es erneut.";
export const FEEDBACK_UPLOAD_FAILED_MESSAGE = "Screenshot oder Bilder konnten nicht gespeichert werden. Bitte erneut senden.";

/** Fehlertext fürs Absenden: bei 503 (Upload fehlgeschlagen, nichts gespeichert) die Backend-Meldung, sonst der Standardtext. */
export function feedbackSubmitErrorMessage(error: unknown): string {
  const status =
    typeof error === "object" && error !== null && "response" in error
      ? (error as { response?: { status?: number } }).response?.status
      : undefined;
  if (status !== 503) return FEEDBACK_SEND_FAILED_MESSAGE;
  return backendErrorMessage(error) ?? FEEDBACK_UPLOAD_FAILED_MESSAGE;
}
